import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';

import { useDeleteShareCategoryMutation } from 'api/main';
import { useDialog, useGetShareSubcategorySubscriptions } from 'utils/hooks';
import { texts } from 'utils/languages/fr';
import { useCategoryContext } from './CategoryContext';

import CategoryForm from './CategoryForm';
import { ConfirmDialog, Loader } from 'components';
import { alpha, Box, Collapse, Paper, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';

const CategoryItem = ({ category, fundId }) => {
    const { editingCategoryId, newCategory, setEditingCategoryId, setNewCategory } =
        useCategoryContext();

    const [deleteShareCategory, { isLoading }] = useDeleteShareCategoryMutation();

    const { open: confirmEditOpen, onClose: handleEditClose, onOpen: handleEditOpen } = useDialog();
    const { open: deleteOpen, onClose: handleDeleteClose, onOpen: handleDeleteOpen } = useDialog();
    const { subscriptions, isLoading: subscriptionsLoading } = useGetShareSubcategorySubscriptions(
        category?.shareSubcategories?.map(sc => sc.id)
    );

    const handleEdit = useCallback(() => {
        setNewCategory(null);
        setEditingCategoryId(category?.id);
        handleEditClose();
    }, [category?.id, setNewCategory, setEditingCategoryId, handleEditClose]);

    const handleConfirmEdit = useCallback(() => {
        if (editingCategoryId || newCategory) {
            handleEditOpen();
        } else {
            handleEdit();
        }
    }, [editingCategoryId, handleEditOpen, handleEdit, newCategory]);

    const handleDelete = useCallback(() => {
        deleteShareCategory(category?.id);
    }, [category?.id, deleteShareCategory]);

    return (
        <Paper
            sx={theme => ({
                padding: editingCategoryId === category?.id ? 2 : 1,
                borderRadius: '3px',
                marginY: 1,
                minWidth: '420px',
                border:
                    editingCategoryId === category?.id
                        ? `3px solid ${alpha(theme.palette.primary.light, 0.5)}`
                        : '',

                backgroundColor:
                    editingCategoryId !== category?.id ? theme.palette.secondary.main : 'initial',
            })}
            elevation={3}
        >
            {editingCategoryId !== category?.id && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <ConfirmDialog
                        open={confirmEditOpen}
                        handleConfirm={handleEdit}
                        handleClose={handleEditClose}
                        title={texts.CHANGES_NOT_CONFIRMED}
                        confirmText={texts.CONFIRMER}
                        cancelText={texts.CANCEL}
                    >
                        {texts.CONFIRM_DISCARD_CHANGES}
                    </ConfirmDialog>
                    <ConfirmDialog
                        open={deleteOpen}
                        handleConfirm={handleDelete}
                        handleClose={handleDeleteClose}
                        title={texts.DELETE_CATEGORY}
                        loading={isLoading}
                        confirmText={texts.CONFIRMER}
                        cancelText={texts.CANCEL}
                    >
                        {texts.CONFIRM_DELETE_CATEGORY}
                    </ConfirmDialog>
                    <Typography title={category?.name} noWrap sx={{ maxWidth: '50%' }}>
                        {category?.name}
                    </Typography>
                    <Loader loading={subscriptionsLoading} marginTop={0} size={20}>
                        <Box display="flex" alignItems="center">
                            <icons.Pencil
                                sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                onClick={handleConfirmEdit}
                            />
                            <icons.Delete
                                sx={{
                                    color: subscriptions?.length > 0 ? 'grey.500' : 'common.black',
                                    cursor: subscriptions?.length > 0 ? 'not-allowed' : 'pointer',
                                    '&:hover': {
                                        color:
                                            subscriptions?.length > 0 ? 'grey.500' : 'error.main',
                                    },
                                }}
                                onClick={subscriptions?.length > 0 ? null : handleDeleteOpen}
                            />
                        </Box>
                    </Loader>
                </Box>
            )}
            <Collapse in={editingCategoryId === category?.id}>
                {editingCategoryId === category?.id && (
                    <CategoryForm
                        category={category}
                        fundId={fundId}
                        onCancel={() => {
                            setEditingCategoryId(null);
                        }}
                    />
                )}
            </Collapse>
        </Paper>
    );
};

CategoryItem.propTypes = {
    category: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        private: PropTypes.bool,
        availableFrom: PropTypes.string,
        availableTo: PropTypes.string,
        shareSubcategories: PropTypes.array,
    }),
    fundId: PropTypes.number,
};

export default CategoryItem;
